<script lang="ts" setup>
import type { PxlIconName } from "@/common/components/U/Icon";

export type Toast = {
  type: "success" | "warning" | "error" | "info";
  message: string;
  title?: string;
  icon?: PxlIconName;
};

const alertMap = reactive(new Map<number, Toast>());

function addToast(toast: Toast) {
  const key = Date.now();
  alertMap.set(key, toast);
  setTimeout(() => alertMap.delete(key), 7000); // auto clear toast with delay
}

const addErrorToast = (message: string, title?: string, icon?: PxlIconName) => addToast({ type: "error", title, message, icon });
const addWarningToast = (message: string, title?: string, icon?: PxlIconName) => addToast({ type: "warning", title, message, icon });
const addSuccessToast = (message: string, title?: string, icon?: PxlIconName) => addToast({ type: "success", title, message, icon });
const addInfoToast = (message: string, title?: string, icon?: PxlIconName) => addToast({ type: "info", title, message, icon });

const colors = {
  error: "red",
  warning: "orange",
  success: "green",
  info: "blue",
} as const;

provideToast({ error: addErrorToast, success: addSuccessToast, warning: addWarningToast, info: addInfoToast });
</script>

<template>
  <div
    v-if="alertMap.size !== 0"
    class="pxl-toast pxl-toast-top pxl-toast-center z-[99] w-full md:w-[560px]"
  >
    <UAlert
      v-for="[key, alert] in alertMap"
      :key="alert.message"
      :icon="alert.icon"
      :color="colors[alert.type]"
      :title="alert.title"
      :description="alert.message"
      class="mx-auto"
      closable
      @close="alertMap.delete(key)"
    />
  </div>
</template>

<style lang="scss">
/* Toast */
.pxl-toast {
  @apply fixed flex flex-col min-w-fit gap-2 p-4;
}
.pxl-toast > * {
  animation: toast-pop 0.25s ease-out;
}
:where(.pxl-toast) {
  @apply right-0 left-auto top-auto bottom-0 translate-x-0 translate-y-0;
}
.pxl-toast:where(.pxl-toast-start) {
  @apply right-auto left-0 translate-x-0;
}
.pxl-toast:where(.pxl-toast-center) {
  @apply right-1/2 left-1/2 -translate-x-1/2;
}
.pxl-toast:where(.pxl-toast-end) {
  @apply right-0 left-auto translate-x-0;
}
.pxl-toast:where(.pxl-toast-bottom) {
  @apply top-auto bottom-0 translate-y-0;
}
.pxl-toast:where(.pxl-toast-middle) {
  @apply top-1/2 bottom-auto -translate-y-1/2;
}
.pxl-toast:where(.pxl-toast-top) {
  @apply top-0 bottom-auto translate-y-0;
}

@keyframes toast-pop {
  0% {
    transform: scale(0.9);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
